import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Tooltip, App as AntdApp } from 'antd';
import {
	DndContext,
	DragOverlay,
	PointerSensor,
	useDraggable,
	useSensor,
	useSensors,
} from '@dnd-kit/core';

import { CLOSE } from '../icons';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import ItemCardsGrid from '../items/ItemCardsGrid';
import { LayoutTierSwitch, TIER_NOTE, layoutTierAccess, preferredTier, tiersBelow } from './layoutTiers';
import { defaultSpanFor, describeSpan, isLongType, TILE_COLS, TILE_MAX_ROWS } from '../../config/tileSlotPolicy';
import { deleteTileLayout, getCardLayouts, putTileLayout } from '../../services/cardLayouts';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import useUndoHistory from '../../hooks/useUndoHistory';
import useUndoShortcuts from '../../hooks/useUndoShortcuts';
import { useScreenShortcuts } from '../../contexts/ShortcutsContext';
import UndoRedoButtons from '../common/UndoRedoButtons';
import { confirmDestructive } from '../common/confirmDestructive';
import ToggleRow from '../common/ToggleRow';
import { elementLabel, tileFieldsByName } from '../items/itemValueDisplay';
import { sampleElementValues } from '../../utils/displaySample';
import {
	applyTileEdit,
	canPlace,
	describePosition,
	dropRefusal,
	emptyBody,
	normalize,
	placedNames,
	planTileDrop,
	resizeRequest,
	rowWidth,
	sameBody,
	setSpan,
	spanLimits,
	tileBodyProblem,
	tileShowsTags,
	withTileTags,
} from './tileLayoutModel';

/**
 * "How this card's items read on a tile" — the editor for one TIER of a card
 * layout (cardLayouts, kind 'tile').
 *
 * Three tiers cascade card -> collection -> user, so the editor always says
 * which one it is writing and what it would fall back to. Saving is
 * immediate: layouts live outside the card's single-draft Save, the way quick
 * add and multi add do, which is why the panel says so out loud.
 *
 * The body is the tile's grid drawn as it is: three rows of six columns
 * (KPR-346), always all three rows, so the shape under the pointer never
 * moves while a field is dragged. Every save is in sixths, `cols: 6`; a stored
 * body that says no `cols: 6` opens as a tier nobody has arranged
 * (tileLayoutModel normalize). Where the field would land is worked out against that
 * fixed grid (planDrop in ./tileLayoutModel) and drawn live — a field dropped
 * on another takes its place and moves it toward where the dragged one came
 * from; the top or bottom edge of a row opens a new row there. Every move
 * also has a pointer-free path: a palette field places on click, a placed
 * one moves with the arrow keys and widens with its width button, which is
 * what the tests drive (a drag cannot be driven in jsdom).
 *
 * What is dragged is the field's own footprint (the owner's note on d04/d05):
 * the lifted field under the pointer is as wide as the columns it would take
 * where it would land, and changes as the landing does; a resize redraws the
 * field at its new width as the edge moves.
 *
 * KPR-347: a dragged edge is a live PREVIEW (the field, its neighbours and
 * the preview tile all drawn at the width it asks for, "3 of 6" on it) and
 * one edit on release; Escape or a lost pointer puts it back. A drop or a
 * width that cannot happen says why — on the lifted field, in the line
 * under the grid, and to the announcer — and every place the keyboard puts
 * a field is announced by row and columns. Every change to the arrangement
 * is ONE edit through `commitEdit` (tileLayoutModel applyTileEdit), the one
 * place undo (KPR-348) records from.
 *
 * KPR-348: Undo and Redo (the buttons by the tile body, Cmd/Ctrl+Z and
 * Shift+Cmd/Ctrl+Z) step through those edits and the Show tags switch. A keyboard
 * pick-up, however many arrows it takes, is ONE step; a tier loading is a reset
 * (a seed, not an edit) and so is closing the dialog.
 */

const PALETTE_PREFIX = 'palette:';
const PLACED_PREFIX = 'placed:';
// What the first empty row says, by how many rows the body already has.
const NEW_ROW_PROMPTS = ['Drop a field here', 'Drop a field here for a second row', 'Drop a field here for a third row'];
// The top and bottom quarter of a row open a new row there.
const EDGE_BAND = 0.25;
// How far outside the grid a pointer may stray and still be aiming at it.
const SLACK = 24;
// A column's pitch when a field has no size to measure (jsdom).
const FALLBACK_PITCH = 50;

// What each kind of edit is called when undo says it undid it.
const EDIT_LABEL = { insert: 'add', remove: 'remove', span: 'width change', drop: 'move', nudge: 'move', restore: 'move' };

// The pointer drags; the keyboard has its own moves (nudge), so dnd-kit's
// pick-up-with-space instructions would describe a sensor this editor lacks.
// The editor announces every place and refusal itself (KPR-347), so dnd-kit's
// own announcements — which read out internal ids — are silenced.
const SILENT = () => undefined;
const DND_ACCESSIBILITY = {
	screenReaderInstructions: {
		draggable: 'Press Space to pick the field up, the arrow keys to move it along its row or to another row, Shift with the left or right arrow to narrow or widen it, Space to drop it and Escape to put it back.',
	},
	announcements: { onDragStart: SILENT, onDragMove: SILENT, onDragOver: SILENT, onDragEnd: SILENT, onDragCancel: SILENT },
};

const ARROWS = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' };

const inside = (rect, x, y, slack = 0) => Boolean(rect)
	&& x >= rect.left - slack && x <= rect.right + slack && y >= rect.top - slack && y <= rect.bottom + slack;

/**
 * The pointer against the grid as it stands: which row, where along it (in
 * columns), and whether it is on a row's edge. Rows are fixed-height tracks,
 * so the answer does not shift as the live preview rearranges their contents.
 */
function hitTest({ grid, palette, x, y, rowCount, fromBody }) {
	// Over the list: a placed field comes off the tile; one from the list is
	// not aimed anywhere yet (undefined: no reason shown, no edit on release).
	if (inside(palette?.getBoundingClientRect(), x, y)) return fromBody ? { type: 'remove' } : undefined;
	if (!grid || !inside(grid.getBoundingClientRect(), x, y, SLACK)) return null;
	const tracks = Array.from(grid.querySelectorAll('[data-track]')).map(node => node.getBoundingClientRect());
	if (!tracks.length) return null;
	const gap = tracks.length > 1 ? tracks[1].top - tracks[0].bottom : 0;
	let index = tracks.findIndex(rect => y < rect.bottom + gap / 2);
	if (index < 0) index = tracks.length - 1;
	const rect = tracks[index];
	const col = Math.max(0, Math.min(TILE_COLS - 0.001, ((x - rect.left) / rect.width) * TILE_COLS));

	if (index >= rowCount) return { type: 'newRow', at: rowCount };
	const row = { type: 'row', row: index, col };
	const f = (y - rect.top) / rect.height;
	// An edge opens a row there — or, with no room for one, is just the row.
	if (f < EDGE_BAND) return { type: 'newRow', at: index, fallback: row };
	if (f > 1 - EDGE_BAND) return { type: 'newRow', at: index + 1, fallback: row };
	return row;
}

const sameTarget = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * How many columns the dragged field takes: where the drag would land it now
 * (the live plan), else the width it has on the tile, else the width it
 * arrives at from the list.
 */
export function draggedSpan({ plan, body, element, dataType }) {
	const inRows = rows => (rows || []).flat().find(p => p.element === element);
	const landing = plan ? inRows(plan.body?.rows) : null;
	if (landing) return landing.span;
	const placed = inRows(body?.rows);
	if (placed) return placed.span;
	return defaultSpanFor(dataType);
}

/**
 * The lifted field's width in pixels: `span` of the grid's columns, gaps
 * included, measured from a row track when the drag began (tile-editor.css:
 * a row's 5px padding each side, 6px between fields). Null when there is
 * nothing to measure (jsdom; a grid not drawn), so the overlay keeps its own
 * width.
 */
export function footprintWidth({ span, trackWidth, gap = 6, padding = 10 }) {
	if (!trackWidth || !span) return null;
	const inner = trackWidth - padding;
	const column = (inner - gap * (TILE_COLS - 1)) / TILE_COLS;
	return Math.max(0, Math.round(column * span + gap * (span - 1)));
}

/** A field in the palette: draggable, and a button that places it. */
function PaletteChip({ element, disabled, reason, onInsert, dragging }) {
	const { attributes, listeners, setNodeRef } = useDraggable({
		id: PALETTE_PREFIX + element.name,
		disabled,
	});
	const hint = describeSpan(element.dataType);
	const chip = (
		<button
			ref={setNodeRef}
			type="button"
			className={[
				'tile-editor__chip',
				disabled ? 'tile-editor__chip--disabled' : '',
				dragging ? 'tile-editor__chip--dragging' : '',
			].filter(Boolean).join(' ')}
			disabled={disabled}
			onClick={() => onInsert(element)}
			{...attributes}
			{...listeners}
		>
			<span className="tile-editor__chip-name">{elementLabel(element)}</span>
			{hint ? <span className="tile-editor__chip-hint">{hint}</span> : null}
		</button>
	);
	return reason ? <Tooltip title={reason}>{<span>{chip}</span>}</Tooltip> : chip;
}

/**
 * A placed field: dragged by its name, moved by the arrow keys, widened by
 * its width button, by Shift and an arrow while picked up, or by dragging its
 * right edge, and removed by its ✕. `landing` draws it where a drag in
 * progress would put it; `resizing` is its edge being dragged, drawn at the
 * width the pointer asks for until the release commits it.
 */
function Placement({
	placement, body, byName, labelOf, locked, landing, displaced, picked, focused, resizing, resizeBusy,
	onEdit, onAnnounce, onPick, onDrop, onPutBack, onResize,
}) {
	const name = placement.element;
	const label = labelOf(name);
	const { attributes, listeners, setNodeRef } = useDraggable({ id: PLACED_PREFIX + name, disabled: locked });
	// The limits come from the arrangement as committed, so a width being
	// previewed never limits itself.
	const { min, max } = spanLimits(body, name, byName);
	const resize = useRef(null);
	const stopEscape = useRef(null);
	const grip = useRef(null);
	const gripRef = useCallback((node) => {
		grip.current = node;
		setNodeRef(node);
	}, [setNodeRef]);

	// A move to another row remounts the field there; the keyboard stays on it.
	useEffect(() => {
		if (focused && grip.current && document.activeElement !== grip.current) grip.current.focus();
	}, [focused]);

	const widthChanged = (result) => {
		if (result.refusal) onAnnounce(result.refusal.message);
		else onAnnounce(`${label} is on ${describePosition(result.body, name)}.`);
	};

	// The grip grammar of every drag in keepr (platform-standards § Drag to
	// reorder): Space or Enter picks the field up, the arrows move it, Space
	// or Enter drops it, Escape puts it back. Shift with Left or Right
	// narrows or widens what is picked up, a column at a time.
	const onKeyDown = (event) => {
		if (locked) return;
		if (event.key === ' ' || event.key === 'Enter') {
			event.preventDefault();
			if (picked) onDrop(name);
			else onPick(name);
			return;
		}
		if (event.key === 'Escape' && picked) {
			event.preventDefault();
			// The modal closes on Escape too; this one is the field's.
			event.stopPropagation();
			onPutBack(name);
			return;
		}
		const direction = ARROWS[event.key];
		if (!direction || !picked) return;
		event.preventDefault();
		if (event.shiftKey && (direction === 'left' || direction === 'right')) {
			widthChanged(onEdit({ type: 'span', element: name, span: placement.span + (direction === 'left' ? -1 : 1) }));
			return;
		}
		const moved = onEdit({ type: 'nudge', element: name, direction });
		if (moved.refusal) {
			onAnnounce(moved.refusal.message);
			return;
		}
		const pushed = moved.displaced.map(labelOf);
		onAnnounce(`${label} is on ${describePosition(moved.body, name)}${pushed.length ? `; ${pushed.join(' and ')} moved to make room` : ''}.`);
	};

	// Dragging the right edge: the edge follows the pointer in whole columns,
	// measured from where the field began (the column pitch read off the
	// field itself: its width at its columns, plus the row's gap), so the
	// width never chases its own edge as the row redraws.
	const finishResize = (commit) => {
		const state = resize.current;
		resize.current = null;
		stopEscape.current?.();
		stopEscape.current = null;
		if (!state) return;
		onResize(null);
		if (commit && state.last !== state.start) widthChanged(onEdit({ type: 'span', element: name, span: state.last }));
	};
	const onResizeDown = (event) => {
		// One edge at a time: a second finger on another field's edge is ignored.
		if (locked || resize.current || resizeBusy || (event.button !== undefined && event.button > 0)) return;
		event.preventDefault();
		event.stopPropagation();
		const field = event.currentTarget.closest('[data-placed]');
		const rect = field?.getBoundingClientRect();
		const gap = field?.parentElement ? parseFloat(window.getComputedStyle(field.parentElement).columnGap) || 0 : 0;
		const pitch = rect?.width ? (rect.width + gap) / placement.span : 0;
		resize.current = {
			x: event.clientX,
			start: placement.span,
			last: placement.span,
			reason: null,
			pitch: pitch > 0 ? pitch : FALLBACK_PITCH,
			pointerId: event.pointerId,
		};
		// Capture keeps the edge following a pointer that strays off it; a
		// pointer the browser no longer tracks cannot be captured, and the
		// resize still works without it.
		const handle = event.currentTarget;
		const { pointerId } = event;
		try { handle.setPointerCapture?.(pointerId); } catch (_) { /* not capturable */ }
		// Escape puts the width back (KPR-625's rule): in the capture phase,
		// and stopped, so the dialog never sees it.
		const onEscape = (key) => {
			if (key.key !== 'Escape') return;
			key.preventDefault();
			key.stopPropagation();
			try { handle.releasePointerCapture?.(pointerId); } catch (_) { /* never captured */ }
			finishResize(false);
		};
		document.addEventListener('keydown', onEscape, true);
		stopEscape.current = () => document.removeEventListener('keydown', onEscape, true);
		onResize({ element: name, span: placement.span, refusal: null });
	};
	const onResizeMove = (event) => {
		const state = resize.current;
		if (!state) return;
		// A second finger on the screen is not this resize.
		if (state.pointerId !== undefined && event.pointerId !== undefined && event.pointerId !== state.pointerId) return;
		// A mouse with no button held is hovering, not dragging (its release was lost).
		if (event.pointerType === 'mouse' && event.buttons === 0) return;
		const asked = state.start + (event.clientX - state.x) / state.pitch;
		const { span, refusal } = resizeRequest(body, name, asked, byName, { labelOf });
		const reason = refusal?.message || null;
		if (span === state.last && reason === state.reason) return;
		state.last = span;
		state.reason = reason;
		onResize({ element: name, span, refusal });
	};
	const onResizeUp = (event, commit) => {
		const state = resize.current;
		if (state?.pointerId !== undefined && event.pointerId !== undefined && event.pointerId !== state.pointerId) return;
		try { event.currentTarget.releasePointerCapture?.(event.pointerId); } catch (_) { /* never captured */ }
		finishResize(commit);
	};
	// A field that goes away mid-resize (another tier, another row) takes its
	// key listener with it and lets go of the editor's resize.
	const releaseOnUnmount = useRef(onResize);
	releaseOnUnmount.current = onResize;
	useEffect(() => () => {
		stopEscape.current?.();
		if (resize.current) {
			resize.current = null;
			releaseOnUnmount.current(null);
		}
	}, []);

	return (
		<div
			className={[
				'tile-editor__placed',
				landing ? 'tile-editor__placed--landing' : '',
				displaced ? 'tile-editor__placed--displaced' : '',
				picked ? 'tile-editor__placed--picked' : '',
				resizing ? 'tile-editor__placed--resizing' : '',
				resizing?.refusal ? 'tile-editor__placed--at-limit' : '',
			].filter(Boolean).join(' ')}
			style={{ '--field-span': placement.span }}
			data-placed={name}
			data-span={placement.span}
		>
			<span
				ref={gripRef}
				className="tile-editor__placed-grip"
				{...attributes}
				{...listeners}
				onKeyDown={onKeyDown}
				// Tabbing away drops it where it is. A blur with nowhere to go is
				// the field remounting on its new row, which keeps it picked up.
				onBlur={(event) => { if (picked && event.relatedTarget) onDrop(name, { quiet: true }); }}
				aria-pressed={picked}
				aria-label={`Move ${label}, ${placement.span} of ${TILE_COLS} columns`}
			>
				<span className="tile-editor__placed-name">{label}</span>
			</span>
			{resizing ? (
				// What the edge is asking for, on the field itself: the space it takes.
				<span className="tile-editor__placed-measure" aria-hidden="true">{placement.span} of {TILE_COLS}</span>
			) : null}
			{locked ? null : (
				<span className="tile-editor__placed-actions">
					<Tooltip title={min === max ? 'No room to widen it on this row' : `Width: ${placement.span} of ${TILE_COLS}`}>
						<button
							type="button"
							className="tile-editor__placed-span"
							aria-label={`Width of ${label}, ${placement.span} of ${TILE_COLS} columns`}
							disabled={min === max}
							onClick={() => widthChanged(onEdit({ type: 'span', element: name, span: placement.span >= max ? min : placement.span + 1 }))}
						>
							{'▮'.repeat(placement.span)}
						</button>
					</Tooltip>
					<button
						type="button"
						className="tile-editor__placed-remove"
						aria-label={`Remove ${label}`}
						onClick={() => {
							const result = onEdit({ type: 'remove', element: name });
							if (!result.refusal) onAnnounce(`${label} is off the tile.`);
						}}
					>
						<CLOSE aria-hidden="true" />
					</button>
				</span>
			)}
			{locked || min === max ? null : (
				<span
					className="tile-editor__placed-resize"
					aria-hidden="true"
					data-resize={name}
					onPointerDown={onResizeDown}
					onPointerMove={onResizeMove}
					onPointerUp={event => onResizeUp(event, true)}
					onPointerCancel={event => onResizeUp(event, false)}
					// The browser let go of the pointer without a release: put the width back.
					onLostPointerCapture={event => onResizeUp(event, false)}
				/>
			)}
		</div>
	);
}

/** A landing spot for a field coming in from the list: drawn, not draggable. */
const Landing = ({ element, span }) => (
	<div className="tile-editor__placed tile-editor__placed--landing" style={{ '--field-span': span }} aria-hidden="true">
		<span className="tile-editor__placed-grip">
			<span className="tile-editor__placed-name">{elementLabel(element)}</span>
		</span>
	</div>
);

export default function TileLayoutModal({
	open,
	onClose,
	card,
	elementDefs = [],
	collection = null,
	collectionId = null,
	sampleItem = null,
	// The feed's item pool, so the preview's title resolves its lookups the
	// way the grid does. Without it a looked-up title reads as a raw id, and a
	// preview that does not match the list is worse than none.
	itemsById = {},
	onSaved,
}) {
	const { message, modal } = AntdApp.useApp();
	const cardId = card?._id ? String(card._id) : null;

	const [tier, setTier] = useState('card');
	const [rows, setRows] = useState(null);      // every tier's stored row
	// The arrangement and the Show tags switch, with their undo history
	// (KPR-348). Tags show on a tile by default (ruled d15); this tier may turn
	// them off.
	const { present, commit, current, undo, redo, reset: seedHistory, canUndo, canRedo } = useUndoHistory(() => ({ body: emptyBody(), showTags: true }));
	const { body, showTags } = present;
	const [saving, setSaving] = useState(false);
	const [loadError, setLoadError] = useState(null);
	// The drag in progress: which field, from where, and where it would land
	// (`target` undefined until the pointer first moves; null is off the tile).
	const [drag, setDrag] = useState(null);
	const [target, setTarget] = useState(undefined);
	// What the live region says; `n` remounts it, so the same sentence twice
	// (two presses against the same edge) is said twice.
	const [announcement, setAnnouncementState] = useState({ text: '', n: 0 });
	const setAnnouncement = useCallback(text => setAnnouncementState(current => ({ text, n: current.n + 1 })), []);
	// The keyboard's pick-up: which field, and the body to put back on Escape.
	const [picked, setPickedState] = useState(null);
	// Read by commitEdit in the same tick the pick-up changes, before a render.
	const pickedNow = useRef(null);
	const setPicked = useCallback((next) => {
		pickedNow.current = next;
		setPickedState(next);
	}, []);
	// A field's edge being dragged: { element, span, refusal } — drawn, not
	// yet committed (KPR-347).
	const [resizing, setResizing] = useState(null);
	// Why the last edit did not happen, shown under the grid until the next one.
	const [notice, setNotice] = useState(null);
	// A row track's width when a drag began: what the lifted field's
	// footprint is measured against.
	const [trackWidth, setTrackWidth] = useState(null);
	const gridRef = useRef(null);
	const paletteRef = useRef(null);

	// The fields a tile can hold: a date range is ONE, its start carrying its
	// end and labelled with both ("Departs / Returns"), found under either
	// half's name (KPR-626) — what the tile itself draws.
	const byName = useMemo(() => tileFieldsByName(elementDefs), [elementDefs]);
	const fields = useMemo(() => [...new Set(byName.values())], [byName]);
	const labelOf = useCallback(name => elementLabel(byName.get(name)) || name, [byName]);

	// Which tiers this caller may write (the tier UI the table's Columns
	// editor shares — ./layoutTiers).
	const { allowed: tierAllowed, refusal: tierRefusal } = useMemo(
		() => layoutTierAccess({ card, collection, collectionId }),
		[card, collection, collectionId]
	);

	const load = useCallback(() => {
		if (!open || !cardId) return;
		setRows(null);
		setLoadError(null);
		getCardLayouts(cardId, { collectionId })
			.then(({ layouts }) => setRows(layouts))
			.catch(error => setLoadError(error));
	}, [open, cardId, collectionId]);

	useEffect(load, [load]);

	// Opening lands on the most specific tier this caller may write, because
	// that is the one they came to change.
	useEffect(() => {
		if (!open) return;
		setTier(preferredTier(tierAllowed));
	}, [open, tierAllowed]);

	const storedFor = useCallback(
		key => (rows || []).find(row => row.scope === key) || null,
		[rows]
	);

	// The tier being edited seeds the editor; a tier that holds nothing starts
	// from what it INHERITS, so "arrange it for me" begins from what is on
	// screen rather than from an empty tile.
	const inherited = useMemo(() => {
		for (const key of tiersBelow(tier)) {
			const row = storedFor(key);
			if (row) return { scope: key, body: row.body };
		}
		return null;
	}, [tier, storedFor]);

	// Loading a tier is a fresh start, not an edit.
	useEffect(() => {
		if (rows === null) return;
		const own = storedFor(tier);
		const seed = own?.body || inherited?.body || emptyBody();
		seedHistory({ body: normalize(seed, byName), showTags: tileShowsTags(seed) });
		setNotice(null);
		setPicked(null);
	}, [rows, tier, storedFor, inherited, byName, seedHistory, setPicked]);

	// Closing the dialog ends the history with no code of its own: opening reads
	// the stored layouts again (`load`), and rows arriving is the seed above,
	// which empties both stacks. (A closed antd dialog's content is frozen, so
	// nothing here could be seen to change while it is shut anyway.)

	// Another tier chosen mid-gesture (a second finger) is a fresh start:
	// nothing held on the last one carries over.
	const chooseTier = useCallback((next) => {
		setTier(next);
		setDrag(null);
		setTarget(undefined);
		setResizing(null);
	}, []);

	const canWriteTier = tierAllowed[tier];
	const locked = !canWriteTier;
	const placed = placedNames(body);
	const palette = fields.filter(def => !placed.has(def.name));

	/**
	 * THE ONE PLACE the arrangement changes (KPR-347): every drop, insert,
	 * removal, width and keyboard move is an edit through applyTileEdit. A
	 * refused edit changes nothing and leaves its reason under the grid.
	 * Undo (KPR-348) records here.
	 */
	const commitEdit = useCallback((edit) => {
		const result = applyTileEdit(current().body, edit, byName, { labelOf });
		if (!result.refusal) {
			// While a field is picked up every edit joins the pick-up's one step
			// (Escape's `restore` then nets to nothing and removes it).
			const pick = pickedNow.current;
			commit(
				latest => ({ ...latest, body: result.body }),
				pick ? { label: 'move', key: `pick:${pick.id}`, within: Infinity } : { label: EDIT_LABEL[edit.type] || 'change' },
			);
		}
		setNotice(result.refusal ? result.refusal.message : null);
		return result;
	}, [current, commit, byName, labelOf]);

	// An edge picked up starts clean: the last edit's reason is not about it.
	const startOrEndResize = useCallback((next) => {
		if (next && !resizing) setNotice(null);
		setResizing(next);
	}, [resizing]);

	// A field placed from the list takes the keyboard with it: its chip has
	// gone, and the next thing to do is usually to move it.
	const [focusPlaced, setFocusPlaced] = useState(null);
	useEffect(() => {
		if (!focusPlaced) return;
		const field = [...(gridRef.current?.querySelectorAll('[data-placed]') || [])].find(node => node.getAttribute('data-placed') === focusPlaced);
		field?.querySelector('.tile-editor__placed-grip')?.focus();
		setFocusPlaced(null);
	}, [focusPlaced]);

	const insert = useCallback((element) => {
		const result = commitEdit({ type: 'insert', element: element.name });
		setAnnouncement(result.refusal
			? result.refusal.message
			: `${labelOf(element.name)} is on the tile, ${describePosition(result.body, element.name)}.`);
		if (!result.refusal) setFocusPlaced(element.name);
	}, [commitEdit, labelOf, setAnnouncement]);

	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

	// What the drag would do if let go now — the live preview, and the drop —
	// or, when it can do nothing there, why.
	const plan = useMemo(() => {
		if (!drag || !target || target.type === 'remove') return null;
		return planTileDrop(body, drag.element, byName, target);
	}, [drag, target, body, byName]);
	const dragRefusal = useMemo(() => {
		if (!drag || target === undefined || plan || target?.type === 'remove') return null;
		return dropRefusal(body, drag.element, byName, target, { labelOf, fromBody: drag.fromBody });
	}, [drag, target, plan, body, byName, labelOf]);
	// What the grid draws: the drag's landing, or the edge's width, or the arrangement.
	const shown = useMemo(() => {
		if (plan) return plan.body;
		if (resizing) return setSpan(body, resizing.element, resizing.span, byName);
		return body;
	}, [plan, resizing, body, byName]);
	const displacedNames = new Set(plan?.displaced || []);

	const pickCount = useRef(0);
	const pick = useCallback((name) => {
		pickCount.current += 1;
		setPicked({ name, original: body, id: pickCount.current });
		setNotice(null);
		setAnnouncement(`Picked up ${labelOf(name)}, on ${describePosition(body, name)}. The arrow keys move it, Shift with the left or right arrow changes its width, Space drops it, Escape puts it back.`);
	}, [body, labelOf, setAnnouncement, setPicked]);
	const drop = useCallback((name, { quiet = false } = {}) => {
		setPicked(null);
		setNotice(null);
		if (!quiet) setAnnouncement(`${labelOf(name)} dropped on ${describePosition(body, name)}.`);
	}, [body, labelOf, setAnnouncement, setPicked]);
	const putBack = useCallback((name) => {
		const result = picked?.original ? commitEdit({ type: 'restore', body: picked.original }) : { body };
		setPicked(null);
		setAnnouncement(`${labelOf(name)} is back on ${describePosition(result.body, name)}.`);
	}, [picked, body, commitEdit, labelOf, setAnnouncement, setPicked]);

	// Undo and redo (KPR-348). A drag, an edge or a pick-up in the air is not a
	// finished edit, so none of them may be stepped over: the buttons and the
	// keys wait until it has landed or been put back. They wait for a save too:
	// what was sent must be what is on screen when the dialog closes.
	const inAir = Boolean(drag || resizing || picked || saving);
	const handleUndo = useCallback(() => {
		const done = undo();
		setNotice(null);
		setAnnouncement(done ? `Undid ${done.label}.` : 'Nothing to undo.');
	}, [undo, setAnnouncement]);
	const handleRedo = useCallback(() => {
		const done = redo();
		setNotice(null);
		setAnnouncement(done ? `Redid ${done.label}.` : 'Nothing to redo.');
	}, [redo, setAnnouncement]);
	useUndoShortcuts({
		enabled: open && !inAir,
		onUndo: handleUndo,
		onRedo: handleRedo,
		// The keys are this dialog's, not those of the reset confirmation or a
		// dropdown opened over it.
		accepts: (target) => {
			if (!target?.closest) return true;
			if (target.closest('.ant-modal-confirm, .ant-select-dropdown, .ant-popover, .ant-dropdown')) return false;
			return Boolean(target.closest('.tile-editor-modal')) || target === document.body || target.classList.contains('ant-modal-wrap');
		},
	});
	useScreenShortcuts(open ? 'Tile layout editor' : null, [
		{ keys: ['mod', 'Z'], label: 'Undo' },
		{ keys: ['mod', 'shift', 'Z'], label: 'Redo' },
	]);

	const endDrag = () => {
		setDrag(null);
		setTarget(undefined);
		setTrackWidth(null);
	};

	const handleDragStart = useCallback(({ active }) => {
		const id = String(active.id);
		const fromBody = id.startsWith(PLACED_PREFIX);
		setDrag({ element: id.slice((fromBody ? PLACED_PREFIX : PALETTE_PREFIX).length), fromBody });
		setTarget(undefined);
		setNotice(null);
		const track = gridRef.current?.querySelector('[data-track]');
		setTrackWidth(track ? track.getBoundingClientRect().width || null : null);
	}, []);

	const handleDragMove = useCallback(({ activatorEvent, delta }) => {
		if (!drag || !activatorEvent) return;
		const point = activatorEvent.touches?.[0] || activatorEvent;
		const next = hitTest({
			grid: gridRef.current,
			palette: paletteRef.current,
			x: point.clientX + delta.x,
			y: point.clientY + delta.y,
			rowCount: body.rows.length,
			fromBody: drag.fromBody,
		});
		setTarget(current => (sameTarget(current, next) ? current : next));
	}, [drag, body.rows.length]);

	const handleDragEnd = useCallback(() => {
		// A drag that never aimed anywhere (released where it began) is no edit.
		if (drag && target !== undefined) {
			const { element, fromBody } = drag;
			const result = commitEdit({ type: 'drop', element, target, fromBody });
			const label = labelOf(element);
			if (result.refusal) {
				const sentence = `${label} was not ${fromBody ? 'moved' : 'placed'}. ${result.refusal.message}`;
				setNotice(sentence);
				setAnnouncement(sentence);
			} else if (target?.type === 'remove') {
				setAnnouncement(`${label} is off the tile.`);
			} else {
				const pushed = result.displaced.map(labelOf);
				setAnnouncement(`${label} placed on ${describePosition(result.body, element)}${pushed.length ? `; ${pushed.join(' and ')} moved to make room` : ''}.`);
			}
		}
		endDrag();
	}, [drag, target, commitEdit, labelOf, setAnnouncement]);

	const own = storedFor(tier);
	const storedShowsTags = tileShowsTags(own ? own.body : inherited?.body);
	const dirty = !sameBody(body, own?.body || (own ? emptyBody() : inherited?.body) || emptyBody()) || !own || showTags !== storedShowsTags;

	// Unsaved work is a change to what the tier seeded — not `dirty`, which
	// is also true of a tier nobody has arranged yet.
	const seededBody = useMemo(
		() => (rows === null ? null : normalize(own?.body || inherited?.body || emptyBody(), byName)),
		[rows, own, inherited, byName]
	);
	const edited = seededBody !== null && (!sameBody(body, seededBody) || showTags !== tileShowsTags(own?.body || inherited?.body));
	// What is saved and previewed: the arrangement plus the tags switch
	// (`tags: false` only when off — a body that shows tags says nothing).
	// Always in sixths (KPR-346): a body with no `cols` is in thirds, which the
	// API refuses from a client.
	const savedBody = useMemo(() => ({ ...withTileTags(body, showTags), cols: TILE_COLS }), [body, showTags]);
	// What the API would refuse in it (a belt: no edit can produce one).
	const problem = useMemo(() => tileBodyProblem(savedBody, byName), [savedBody, byName]);
	const { allowNavigation, guardDiscard } = useDirtyForm(edited, { enabled: open });
	// Cancel, the ✕, the mask and Esc all ask first when something was arranged.
	const handleCancel = useCallback(() => guardDiscard(() => onClose?.()), [guardDiscard, onClose]);

	const save = async () => {
		setSaving(true);
		try {
			await putTileLayout(cardId, { scope: tier, collectionId: tier === 'card' ? null : collectionId, body: savedBody });
			message.success('Tile layout saved.');
			allowNavigation();
			onSaved?.();
			// A save finishes the modal (platform-standards §8).
			onClose?.();
		} catch (error) {
			message.error(error?.response?.data?.message || 'Saving the tile layout failed.');
		} finally {
			setSaving(false);
		}
	};

	const reset = async () => {
		setSaving(true);
		try {
			await deleteTileLayout(cardId, { scope: tier, collectionId: tier === 'card' ? null : collectionId });
			message.success(inherited ? `Reset to the ${inherited.scope} layout.` : 'Reset to automatic.');
			allowNavigation();
			onSaved?.();
			// A save finishes the modal (platform-standards §8).
			onClose?.();
		} catch (error) {
			message.error(error?.response?.data?.message || 'Resetting the tile layout failed.');
		} finally {
			setSaving(false);
		}
	};

	// Reset drops this tier's row; asked first, since what was arranged here
	// goes with it (History can put it back: `cardLayout.delete`).
	const confirmReset = () => confirmDestructive(modal, {
		title: 'Reset this tile layout?',
		content: `This tier's arrangement is removed and ${inherited ? `the ${inherited.scope} layout` : 'the automatic layout'} applies again. History can restore it.`,
		okText: 'Reset',
		onOk: reset,
	});

	// The preview is the REAL tile at its real size. A caller with a record to
	// hand (the items page) passes one, because an arrangement is judged on
	// actual values; the card page has none, so the same sample machinery the
	// display-template preview uses stands in.
	const previewItems = useMemo(() => {
		if (sampleItem) return [sampleItem];
		if (!cardId) return [];
		return [{
			_id: 'tile-layout-preview',
			card_id: cardId,
			collection_id: collectionId,
			elements: sampleElementValues(elementDefs),
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
		}];
	}, [sampleItem, cardId, collectionId, elementDefs]);

	// A long text alone across the tile is its paragraph, not a field.
	const paragraph = body.rows.length === 1 && body.rows[0].length === 1
		&& body.rows[0][0].span >= TILE_COLS && isLongType(byName.get(body.rows[0][0].element)?.dataType);
	const dragElement = drag ? byName.get(drag.element) : null;
	const dragSpan = drag ? draggedSpan({ plan, body, element: drag.element, dataType: dragElement?.dataType }) : null;
	const dragWidth = drag ? footprintWidth({ span: dragSpan, trackWidth }) : null;
	// The row a refused drop was aimed at turns red; its reason is on the lifted field.
	let refusedRow = null;
	if (dragRefusal && target?.type === 'row') refusedRow = target.row;
	else if (dragRefusal && target?.type === 'newRow' && target.fallback) refusedRow = target.fallback.row;
	// Why nothing would happen, while it would not; else why the last edit did not.
	const reason = dragRefusal?.message || resizing?.refusal?.message || (drag || resizing ? null : notice);

	const renderTrack = (rowIndex) => {
		const row = shown.rows[rowIndex];
		const firstEmpty = rowIndex === shown.rows.length;
		const classes = [
			'tile-editor__row',
			row ? '' : 'tile-editor__row--empty',
			refusedRow === rowIndex ? 'tile-editor__row--refused' : '',
		].filter(Boolean).join(' ');
		if (!row) {
			return (
				<div key={`track-${rowIndex}`} className={classes} data-track={rowIndex} aria-label={`Row ${rowIndex + 1}, empty`}>
					{firstEmpty && !locked ? (
						<span className="tile-editor__row-empty">{NEW_ROW_PROMPTS[rowIndex] || NEW_ROW_PROMPTS[0]}</span>
					) : null}
				</div>
			);
		}
		const free = TILE_COLS - rowWidth(row);
		return (
			<div key={`track-${rowIndex}`} className={classes} data-track={rowIndex} aria-label={`Row ${rowIndex + 1}`}>
				{row.map(placement => {
					const isDragged = drag && placement.element === drag.element;
					if (isDragged && !drag.fromBody) {
						return <Landing key={placement.element} element={dragElement} span={placement.span} />;
					}
					return (
						<Placement
							key={placement.element}
							placement={placement}
							body={body}
							byName={byName}
							labelOf={labelOf}
							locked={locked}
							landing={Boolean(isDragged)}
							displaced={displacedNames.has(placement.element)}
							picked={picked?.name === placement.element}
							focused={picked?.name === placement.element}
							resizing={resizing?.element === placement.element ? resizing : null}
							resizeBusy={Boolean(resizing) && resizing.element !== placement.element}
							onEdit={commitEdit}
							onAnnounce={setAnnouncement}
							onPick={pick}
							onDrop={drop}
							onPutBack={putBack}
							onResize={startOrEndResize}
						/>
					);
				})}
				{Array.from({ length: free }, (_, i) => <span key={`free-${i}`} className="tile-editor__slot" aria-hidden="true" />)}
			</div>
		);
	};

	return (
		<Modal
			open={open}
			onCancel={handleCancel}
			title="Tile layout"
			width={760}
			className="tile-editor-modal"
			// Escape during a drag or a resize puts the field back; it does not close the dialog.
			keyboard={!drag && !resizing}
			footer={[
				own ? (
					<Button key="reset" danger onClick={confirmReset} disabled={!canWriteTier || saving}>
						{inherited ? `Reset to ${inherited.scope}` : 'Reset to automatic'}
					</Button>
				) : null,
				<Button key="cancel" onClick={handleCancel}>Cancel</Button>,
				<Button key="save" type="primary" loading={saving} disabled={!canWriteTier || !dirty || Boolean(problem)} onClick={save}>
					Save
				</Button>,
			].filter(Boolean)}
		>
			<LayoutTierSwitch value={tier} onChange={chooseTier} allowed={tierAllowed} refusal={tierRefusal} />
			<p className="form-panel__hint">
				{canWriteTier ? `${TIER_NOTE[tier]} Changes on this tab apply immediately.` : tierRefusal[tier]}
			</p>

			<DialogLoadState
				loaded={rows !== null}
				error={Boolean(loadError)}
				label="this card's layouts"
				onRetry={load}
				skeleton={<Skeleton variant='form-field' count={4} />}
			>
				<DndContext
					sensors={sensors}
					autoScroll={false}
					accessibility={DND_ACCESSIBILITY}
					onDragStart={handleDragStart}
					onDragMove={handleDragMove}
					onDragEnd={handleDragEnd}
					onDragCancel={endDrag}
				>
					<div className={`tile-editor${locked ? ' tile-editor--locked' : ''}`}>
						<div
							ref={paletteRef}
							className={`tile-editor__palette${drag?.fromBody && target?.type === 'remove' ? ' tile-editor__palette--remove' : ''}`}
						>
							<div className="micro-label">Fields</div>
							{palette.length ? palette.map(element => {
								const where = canPlace(body, element.name, byName);
								const full = !where.rows.length && !where.newRow;
								return (
									<PaletteChip
										key={element.name}
										element={element}
										disabled={full || locked}
										dragging={drag?.element === element.name}
										reason={full && !locked ? 'The tile is full — remove or narrow a field first.' : null}
										onInsert={insert}
									/>
								);
							}) : <p className="form-panel__hint">Every field is on the tile.</p>}
							{drag?.fromBody ? (
								<p className="tile-editor__palette-drop">Drop here to take it off the tile</p>
							) : null}
						</div>

						<div className="tile-editor__body">
							<div className="tile-editor__bar">
								<div className="micro-label">Tile body</div>
								{locked ? null : (
									<UndoRedoButtons canUndo={canUndo} canRedo={canRedo} onUndo={handleUndo} onRedo={handleRedo} disabled={inAir} />
								)}
							</div>
							<div ref={gridRef} className="tile-editor__grid" role="group" aria-label="Tile body, three rows of six columns">
								{Array.from({ length: TILE_MAX_ROWS }, (_, rowIndex) => renderTrack(rowIndex))}
							</div>
							{paragraph ? (
								<p className="form-panel__hint">
									Alone across the tile, {elementLabel(byName.get(body.rows[0][0].element))} reads as a three-line paragraph.
								</p>
							) : body.rows.length > 1 ? (
								<p className="form-panel__hint">
									{body.rows.length > 2 ? 'Three' : 'Two'} rows make every tile in the list taller, so the tiles stay one height.
								</p>
							) : null}
							{locked ? null : reason ? (
								// The how-to's line, so the grid above never moves when a reason shows.
								<p className="form-panel__hint tile-editor__how tile-editor__reason" data-reason>{reason}</p>
							) : (
								<p className="form-panel__hint tile-editor__how">
									Drop a field on another to swap them. A row’s top or bottom edge opens a new row there. Drag a field’s right edge to change its width.
								</p>
							)}
							{problem && !locked ? (
								<p className="form-panel__hint tile-editor__reason">This arrangement cannot be saved: {problem}</p>
							) : null}
						</div>
					</div>
					<DragOverlay dropAnimation={null}>
						{dragElement ? (
							<div
								className={[
									'tile-editor__chip',
									'tile-editor__chip--overlay',
									dragWidth ? 'tile-editor__chip--footprint' : '',
									dragRefusal ? 'tile-editor__chip--refused' : '',
								].filter(Boolean).join(' ')}
								style={dragWidth ? { width: dragWidth } : undefined}
								data-span={dragSpan}
							>
								<span className="tile-editor__chip-name">{elementLabel(dragElement)}</span>
								{/* Under the pointer: the space it would take, or why it cannot land there. */}
								<span className="tile-editor__chip-hint">{dragRefusal ? dragRefusal.message : `${dragSpan} of ${TILE_COLS} columns`}</span>
							</div>
						) : null}
					</DragOverlay>
				</DndContext>
			</DialogLoadState>
			<div className="tile-editor__live" aria-live="polite"><span key={announcement.n}>{announcement.text}</span></div>

			{rows === null || loadError ? null : (
				<ToggleRow
					id="tile-show-tags"
					label="Show tags"
					description="Up to three of the item’s tags, under its fields."
					checked={showTags}
					onChange={value => commit(latest => ({ ...latest, showTags: value }), { label: 'tags change' })}
					disabled={!canWriteTier}
					className="tile-editor__tags"
				/>
			)}

			{previewItems.length ? (
				<div className="tile-editor__preview">
					<div className="micro-label">Preview</div>
					<ItemCardsGrid
						items={previewItems}
						cardsById={{ [cardId]: card }}
						itemsById={itemsById}
						collectionId={collectionId}
						collection={collection}
						getElementsForCard={() => elementDefs}
						tileLayouts={{ [cardId]: { body: withTileTags(shown, showTags) } }}
						readOnly
						emptyText={null}
					/>
				</div>
			) : null}
		</Modal>
	);
}
